import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import type { AnalysisData, AnalysisQuestion, AnalysisSubmission } from "@/features/scripts/types";

interface AnalysisTableProps {
  analysisData: AnalysisData;
  formatDate: (dateString: string) => string;
  getScoreBadgeVariant: (score: number) => "default" | "secondary" | "destructive";
  calculateSubmissionScore: (submission: AnalysisSubmission) => number;
  onDeleteSubmission?: (submissionId: string) => void;
  isDeletingSubmission?: boolean;
}

export const AnalysisTable = ({ 
  analysisData, 
  formatDate, 
  getScoreBadgeVariant, 
  calculateSubmissionScore,
  onDeleteSubmission,
  isDeletingSubmission = false,
}: AnalysisTableProps) => {
  const [deleteConfirmSubmissionId, setDeleteConfirmSubmissionId] = useState<string | null>(null);

  const handleDeleteClick = (submissionId: string) => {
    setDeleteConfirmSubmissionId(submissionId);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmSubmissionId && onDeleteSubmission) {
      onDeleteSubmission(deleteConfirmSubmissionId);
      setDeleteConfirmSubmissionId(null);
    }
  };

  const getScoreBadgeStyle = (score: number) => {
    if (score >= 4.0) return "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800";
    if (score >= 3.0) return "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800";
    return "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800";
  };

  const renderQuestionRow = (question: AnalysisQuestion) => (
    <tr key={question.id} className="border-b hover:bg-muted/30 print:hover:bg-transparent">
      <td className="sticky left-0 z-10 bg-background p-2 md:p-3 align-top shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
        <div className="space-y-1">
          <div className="text-sm font-medium">{question.question_name}</div>
          <div className="text-xs text-muted-foreground tabular-nums">
            {question.weight_percentage ? `Weight: ${question.weight_percentage}%` : "Unweighted"}
          </div>
        </div>
      </td>
      {analysisData.submissions.map((submission) => {
        const answer = submission.answers.find((entry) => entry.question_id === question.id);
        return (
          <td key={`${submission.id}-${question.id}`} className="p-2 md:p-3 text-center">
            {answer ? (
              <Badge variant="outline" className={`tabular-nums font-semibold ${getScoreBadgeStyle(answer.score)}`}>
                {answer.score}/5
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </td>
        );
      })}
      <td className="sticky right-0 z-10 bg-background p-2 md:p-3 text-center shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
        <Badge variant="outline" className={`tabular-nums font-bold ${getScoreBadgeStyle(analysisData.averages[question.id] || 0)}`}>
          {(analysisData.averages[question.id] || 0).toFixed(1)}/5
        </Badge>
      </td>
    </tr>
  );

  return (
    <>
      <div className="overflow-x-auto md:overflow-visible -mx-6 md:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full border-collapse">
        <thead>
          <tr className="border-b">
            <th className="sticky left-0 z-10 bg-background min-w-[140px] md:min-w-[200px] p-2 md:p-3 text-left font-medium shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">Details</th>
            {analysisData.submissions.map((submission) => (
              <th key={submission.id} className="min-w-[140px] md:min-w-[160px] p-2 md:p-3 text-left font-medium">
                <div className="space-y-1">
                  <div className="text-sm font-semibold">{submission.reviewer_name}</div>
                  <div className="text-xs text-muted-foreground">{submission.reviewer_email}</div>
                </div>
              </th>
            ))}
            <th className="sticky right-0 z-10 bg-background min-w-[100px] md:min-w-[120px] p-2 md:p-3 text-left font-medium shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">Average</th>
          </tr>
        </thead>
      <tbody>
        <tr className="border-b">
          <td className="sticky left-0 z-10 bg-background p-2 md:p-3 text-xs md:text-sm font-medium text-muted-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">Received At</td>
          {analysisData.submissions.map((submission) => (
            <td key={submission.id} className="p-2 md:p-3 text-xs md:text-sm text-muted-foreground">
              {formatDate(submission.submitted_at)}
            </td>
          ))}
          <td className="sticky right-0 z-10 bg-background p-2 md:p-3 text-xs md:text-sm text-muted-foreground shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">-</td>
        </tr>
        <tr className="border-b">
          <td className="sticky left-0 z-10 bg-background p-2 md:p-3 text-xs md:text-sm font-medium text-muted-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">Notes</td>
          {analysisData.submissions.map((submission) => (
            <td key={submission.id} className="p-2 md:p-3 text-xs md:text-sm align-top">
              <div className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words print:max-h-none print:overflow-visible">
                {submission.notes || "-"}
              </div>
            </td>
          ))}
          <td className="sticky right-0 z-10 bg-background p-2 md:p-3 text-xs md:text-sm text-muted-foreground shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">-</td>
        </tr>
        {analysisData.questions.map(renderQuestionRow)}
        <tr className="bg-primary/5 font-bold print:bg-transparent">
          <td className="sticky left-0 z-10 bg-primary/5 p-2 md:p-3 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
            <strong className="text-primary">Overall Score</strong>
          </td>
          {analysisData.submissions.map((submission) => {
            const submissionScore = calculateSubmissionScore(submission);
            return (
              <td key={submission.id} className="p-2 md:p-3 text-center">
                <span className="text-sm md:text-base font-semibold">
                  {submissionScore ? `${submissionScore.toFixed(1)}/5` : "-"}
                </span>
              </td>
            );
          })}
          <td className="sticky right-0 z-10 bg-primary/5 p-2 md:p-3 text-center shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
            <Badge variant="outline" className={`px-4 py-1 text-lg tabular-nums font-extrabold ${getScoreBadgeStyle(analysisData.weightedOverallScore)}`}>
              {analysisData.weightedOverallScore.toFixed(1)}/5
            </Badge>
          </td>
        </tr>
        {onDeleteSubmission && (
          <tr className="border-t">
            <td className="sticky left-0 z-10 bg-background p-2 md:p-3 text-xs md:text-sm font-medium text-muted-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">Delete Submission</td>
            {analysisData.submissions.map((submission) => (
              <td key={submission.id} className="p-2 md:p-3 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDeleteClick(submission.id)}
                  disabled={isDeletingSubmission}
                  title="Delete submission"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </td>
            ))}
            <td className="sticky right-0 z-10 bg-background p-2 md:p-3 text-center shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
              <span className="text-sm text-muted-foreground">-</span>
            </td>
          </tr>
        )}
      </tbody>
    </table>
        </div>
      </div>

    <AlertDialog open={!!deleteConfirmSubmissionId} onOpenChange={(open) => !open && setDeleteConfirmSubmissionId(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Submission</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this feedback submission? This action cannot be undone and will permanently remove all answers from this reviewer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeletingSubmission}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            disabled={isDeletingSubmission}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeletingSubmission ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
